import dayjs from "dayjs";
import {
  ACTIVE_LK,
  APPROVED_LK,
  APPROVED_SK,
  CANCELLED_SK,
  CONFIRMED_SK,
  DISCREPANCY_DAMAGED_LK,
  DISCREPANCY_DAMAGED_SK,
  DISCREPANCY_OVERAGE_LK,
  DISCREPANCY_OVERAGE_SK,
  DISCREPANCY_SHORTAGE_LK,
  DISCREPANCY_SHORTAGE_SK,
  FULFILLED_SK,
  INACTIVE_LK,
  OUT_OF_STOCK_SK,
  PARTIAL_SK,
  PENDING_SK,
  PREPARED_SK,
  RECEIVED_SK,
  REJECTED_SK,
  SHIPPED_SK,
} from "../constants";

export const getStatus = (status) => {
  return status ? ACTIVE_LK : INACTIVE_LK;
};

/**
 * Formats a number with thousand separators and optional decimal stripping.
 * @param {number|string} value
 * @returns {string}
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '';

  const stringValue = value.toString();
  const parts = stringValue.split('.');

  // Format integer part
  const num = Number(parts[0]);
  if (isNaN(num)) return stringValue;

  let formatted = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Add decimal part back
  if (parts.length > 1) {
    const decimalPart = parts[1];

    // If it's all zeros and longer than 1 digit (e.g. .00), strip it.
    // This removes the redundant .00 from server responses but allows typing .0
    if (/^0+$/.test(decimalPart) && decimalPart.length > 1) {
      return formatted;
    }

    formatted += '.' + decimalPart;
  } else if (stringValue.endsWith('.')) {
    formatted += '.';
  }

  return formatted;
};

/**
 * Format a quantity for display with at most two decimal places.
 * This is display-only; it does not change the original value.
 *
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export const formatQuantity = (value) => {
  if (value === null || value === undefined || value === '') return '0';

  const match = String(value).trim().match(/^([+-]?)(\d+)(?:\.(\d*))?$/);
  if (!match) return String(value);

  const [, sign, wholePart, fractionPart = ''] = match;
  let whole = wholePart.replace(/^0+(?=\d)/, '');
  let fraction = (fractionPart + '00').slice(0, 2);

  if (fractionPart[2] && fractionPart[2] >= '5') {
    const digits = `${whole}${fraction}`.split('');
    for (let index = digits.length - 1; index >= 0; index -= 1) {
      if (digits[index] === '9') {
        digits[index] = '0';
      } else {
        digits[index] = String(Number(digits[index]) + 1);
        break;
      }
    }

    if (digits[0] === '0') digits.unshift('1');
    whole = digits.slice(0, -2).join('');
    fraction = digits.slice(-2).join('');
  }

  fraction = fraction.replace(/0+$/, '');
  return `${sign === '-' && (whole !== '0' || fraction) ? '-' : ''}${whole}${
    fraction ? `.${fraction}` : ''
  }`;
};

/**
 * Strips commas from a string to get a raw numeric value.
 * @param {string} value
 * @returns {string}
 */
export const stripCommas = (value) => {
  if (!value) return '';
  return value.toString().replace(/,/g, '');
};

/**
 * Use this method to show formatted price in application
 *
 * @param {string|number} num
 *
 * @return {string} formatted price
 */
export function getFormattedPrice(num) {
  if (num === null || num === undefined || num === '') {
    return '0';
  }
  // Use parseFloat().toString() to strip redundant trailing zeros for display
  return formatNumber(parseFloat(num).toString());
}


/**
 * Use this method to show date time in application
 *
 * @param {String} date - ISO 8601 or RFC 2822 format
 * @return {string} formatted date
 */
export function getFormattedDateTime(date) {
  return dayjs(date).format('DD/MM/YYYY HH:mm:ss');
}

/**
 * Use this method to show date in application
 *
 * @param {String} date - ISO 8601 or RFC 2822 format
 * @return {string} formatted date
 */
export function getFormattedDate(date) {
  return dayjs(date).format('DD/MM/YYYY hh:mm A');
}

export function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case RECEIVED_SK:
    case APPROVED_SK:
    case 'accepted':
      return '#10B981'; // Success Green
    case REJECTED_SK:
    case 'rejected':
      return '#EF4444'; // Error Red
    case PENDING_SK:
    case 'requested':
      return '#F59E0B'; // Warm Amber/Yellow
    case CONFIRMED_SK:
      return '#3B82F6'; // Professional Blue
    case PREPARED_SK:
      return '#06B6D4'; // Modern Cyan
    case SHIPPED_SK:
      return '#8B5CF6'; // Soft Purple
    case CANCELLED_SK:
      return '#EF4444'; // Error Red
    case OUT_OF_STOCK_SK:
      return '#DC2626'; // Strong Red (Stock Issue)
    case PARTIAL_SK:
      return '#F97316'; // Orange (Partial Fulfillment)
    case FULFILLED_SK:
      return '#10B981'; // Success Green
    case '':
    default:
      return '#94A3B8'; // Slate Grey
  }
}

/**
 * Map discrepancy API type values to user-facing labels.
 * API/DB values stay: shortage | overage | damaged
 *
 * @param {string|null|undefined} type
 * @returns {string}
 */
export function getDiscrepancyTypeLabel(type) {
  switch ((type || '').toLowerCase()) {
    case DISCREPANCY_SHORTAGE_SK:
      return DISCREPANCY_SHORTAGE_LK;
    case DISCREPANCY_OVERAGE_SK:
      return DISCREPANCY_OVERAGE_LK;
    case DISCREPANCY_DAMAGED_SK:
      return DISCREPANCY_DAMAGED_LK;
    default:
      return type || '';
  }
}
